/**
 * Pure drive-maths for the Explore nav mode (issue #142).
 *
 * Explore is Orbit's drag/zoom feel plus keyboard driving of the orbit focus:
 * WASD trucks the focus across the terrain (view-relative), Q/E raise/lower the
 * camera, ←/→ rotate the azimuth around the focus and ↑/↓ tilt it. This module
 * holds only the geometry — no three.js, no DOM — so the movement feel is
 * unit-testable in plain Node, mirroring the groundLock.ts / routePlayer split
 * of pure maths from the CameraRig wrapper that mutates the live camera.
 *
 * Explore is interactive camera furniture only; nothing here feeds any LOS
 * result (hard constraint #1, trivially held — this module never touches the
 * analytical core). Ground-height decisions (never fabricate a height at the
 * terrain edge, hard constraints #6/#7) live in CameraRig where the collider
 * sampler is; this module only produces displacements the rig then validates.
 */

/** Horizontal (ground-plane) direction/displacement. */
export interface Vec2 {
  x: number;
  z: number;
}

/** A 3D offset (camera − target), engine-agnostic like groundLock's WalkPose. */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Truck/raise speed as a fraction of the current camera→target distance per
 * second: panning a zoomed-out view covers ground proportionally faster, so
 * the focus takes a similar wall-clock time to cross the frame at every zoom.
 */
export const EXPLORE_PAN_RATE_PER_S = 0.6;
/** Floor so a hard-zoomed-in view (tiny distance) still trucks at a usable
 * speed rather than crawling to a near-stop. Metres/second. */
export const EXPLORE_MIN_PAN_SPEED_M_S = 8;
/** Ceiling so a fully zoomed-out view can't fling the focus across the whole
 * AOI in a single key tap. Metres/second. */
export const EXPLORE_MAX_PAN_SPEED_M_S = 1200;

/** Arrow-key azimuth (←/→) and tilt (↑/↓) angular rates, radians/second. Kept
 * distinct from Fly/Walk's ARROW_LOOK_* rates (which rotate the camera in
 * place) rather than shared: Explore's arrows orbit the camera around the
 * focus, a different motion, so tuning one must never silently change the
 * other (the same "kept distinct" reasoning as walk vs. fly scratch vectors). */
export const EXPLORE_AZIMUTH_RATE = 1.2;
export const EXPLORE_TILT_RATE = 0.9;

/** Minimum polar angle (angle from straight-up +Y) for the tilt clamp: keeps
 * the camera just off a perfectly top-down gimbal, because azimuth (←/→)
 * rotates a purely vertical camera→focus offset about the vertical axis and is
 * a geometric no-op there. Load-bearing — this is what fixed the #142 azimuth
 * dead zone. Radians (~7°). */
export const EXPLORE_MIN_POLAR = 0.12;

/**
 * Static tilt ceiling: exactly level, i.e. the eye in the focus's own
 * horizontal plane (issue #186, up from a flat 1.45 rad / 83.1° which barred
 * the last 6.9° before horizontal). Used directly on entry into Explore and as
 * the fallback whenever the dynamic ceiling below cannot be computed — never
 * past level on ground the rig has not verified (hard constraint #7).
 */
export const EXPLORE_MAX_POLAR_LEVEL = Math.PI / 2;

/**
 * Hard cap on the dynamic ceiling: 135°, i.e. 45° past level. Past-level tilt
 * exists in proportion to how far the pivot has been raised (Shift+E, issue
 * #184), and this bounds it however far the climb goes — a viewer looking more
 * than 45° upward has left "a view of the landscape" behind.
 */
export const EXPLORE_MAX_POLAR_HARD_CAP = (3 * Math.PI) / 4;

const EPS = 1e-6;

/** Which movement keys are held this frame (already resolved from key codes). */
export interface ExploreKeyState {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Truck/raise speed (m/s) for the current camera→target distance, clamped to
 * a usable band. A non-finite or non-positive distance (degenerate, e.g. the
 * boot pose where camera and focus momentarily coincide) falls back to the
 * minimum rather than producing NaN or zero.
 */
export function explorePanSpeed(distance: number): number {
  if (!Number.isFinite(distance) || distance <= 0) return EXPLORE_MIN_PAN_SPEED_M_S;
  return clamp(distance * EXPLORE_PAN_RATE_PER_S, EXPLORE_MIN_PAN_SPEED_M_S, EXPLORE_MAX_PAN_SPEED_M_S);
}

/**
 * Applied altitude change for the Shift+E/Q elevator (issue #184), given the
 * current offset and the raw requested change. The offset is how far the
 * Explore pivot sits ABOVE the terrain, so its floor is 0: a descent that would
 * take the pivot under the surface is trimmed to the part that fits, and a
 * descent from an already-seated pivot returns 0 (the caller then moves
 * nothing). There is no ceiling — going up is always allowed.
 *
 * Returns the delta to apply to BOTH the camera and the pivot, so the caller
 * can move them together and leave the camera→pivot vector untouched. A
 * non-finite request moves nothing rather than poisoning the offset with NaN.
 */
export function exploreAltitudeDelta(offsetM: number, requestedDeltaM: number): number {
  if (!Number.isFinite(offsetM) || !Number.isFinite(requestedDeltaM)) return 0;
  return Math.max(0, offsetM + requestedDeltaM) - offsetM;
}

/**
 * The largest polar angle the tilt may reach and still leave the camera at
 * least `clearanceM` above the ground beneath it (issue #186):
 *
 *   ceiling = acos( clamp((groundAtCameraM + clearanceM - pivotY) / radius, -1, 1) )
 *
 * capped at EXPLORE_MAX_POLAR_HARD_CAP. On flat terrain this reduces to
 * `acos((clearanceM - offset) / radius)`: level when the pivot is seated on the
 * surface, opening up past level exactly in proportion to how far the Shift+E
 * elevator has raised it. Clamping the CONTROL here — rather than raising the
 * old constant and letting the tilt push the eye into the camera floor — is
 * what keeps `applyCameraFloor` a backstop instead of the primary corrector,
 * which is the issue #50 runaway (and it shows up as judder, not as a failed
 * assertion).
 *
 * Every degenerate case returns the static level ceiling, and always a FINITE
 * angle: a NaN written into OrbitControls' maxPolarAngle would silently
 * disable the clamp altogether, which is the worst available failure. That
 * covers a null ground sample (unloaded tile / off-AOI — a transient streaming
 * state, so no console noise), a zero or non-finite radius (camera and pivot
 * momentarily coincident), and any non-finite height. The floor of
 * EXPLORE_MIN_POLAR keeps the clamp range from inverting when the ground under
 * the camera towers over the pivot.
 */
export function exploreTiltCeiling(
  groundAtCameraM: number | null,
  pivotY: number,
  radius: number,
  clearanceM: number,
): number {
  if (groundAtCameraM === null) return EXPLORE_MAX_POLAR_LEVEL;
  if (!Number.isFinite(groundAtCameraM) || !Number.isFinite(pivotY) || !Number.isFinite(clearanceM)) {
    return EXPLORE_MAX_POLAR_LEVEL;
  }
  if (!Number.isFinite(radius) || radius <= EPS) return EXPLORE_MAX_POLAR_LEVEL;
  const ceiling = Math.acos(clamp((groundAtCameraM + clearanceM - pivotY) / radius, -1, 1));
  if (!Number.isFinite(ceiling)) return EXPLORE_MAX_POLAR_LEVEL;
  return clamp(ceiling, EXPLORE_MIN_POLAR, EXPLORE_MAX_POLAR_HARD_CAP);
}

/**
 * Unit horizontal heading the focus should truck along: the camera forward
 * direction projected onto the ground plane. When forward is (near) vertical
 * — the top-down boot pose — its ground projection collapses, so the heading
 * falls back to the camera's up vector projected to the ground (screen-up maps
 * to "away" on the ground when looking straight down). Returns null only if
 * both are vertical, in which case the caller simply doesn't move (never a
 * fabricated heading).
 */
export function exploreHeading(fwd: Vec3, up: Vec3): Vec2 | null {
  const fLen = Math.hypot(fwd.x, fwd.z);
  if (fLen > EPS) return { x: fwd.x / fLen, z: fwd.z / fLen };
  const uLen = Math.hypot(up.x, up.z);
  if (uLen > EPS) return { x: up.x / uLen, z: up.z / uLen };
  return null;
}

/**
 * View-relative horizontal displacement for the held WASD keys this frame.
 * Forward/back run along `heading`; left/right strafe ±90° of it. Diagonals
 * are normalised so pressing two keys never moves faster than one. Returns
 * null when the combined vector is zero (no keys, or opposing keys cancelling).
 */
export function exploreTruckDelta(heading: Vec2, keys: ExploreKeyState, speed: number, dt: number): Vec2 | null {
  // Right = heading rotated +90° about Y: (hx,hz) → (-hz, hx).
  const rightX = -heading.z;
  const rightZ = heading.x;
  let mx = 0;
  let mz = 0;
  if (keys.forward) {
    mx += heading.x;
    mz += heading.z;
  }
  if (keys.back) {
    mx -= heading.x;
    mz -= heading.z;
  }
  if (keys.right) {
    mx += rightX;
    mz += rightZ;
  }
  if (keys.left) {
    mx -= rightX;
    mz -= rightZ;
  }
  const len = Math.hypot(mx, mz);
  if (len < EPS) return null;
  const scale = (speed * dt) / len;
  return { x: mx * scale, z: mz * scale };
}

/**
 * Rotate an offset (camera − target) about the world Y axis by `angleRad`,
 * preserving its radius and height — the ←/→ azimuth orbit. Positive angle
 * uses the standard right-handed Y rotation (x,z) → (x·cos + z·sin, −x·sin + z·cos).
 */
export function rotateAzimuthY(offset: Vec3, angleRad: number): Vec3 {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  return {
    x: offset.x * cos + offset.z * sin,
    y: offset.y,
    z: -offset.x * sin + offset.z * cos,
  };
}

/**
 * Tilt an offset by changing its polar angle (angle from +Y) by `dPolar`,
 * clamped to [minPolar, maxPolar], while preserving radius and azimuth — the
 * ↑/↓ tilt. Increasing polar drops the camera toward the focus's horizontal
 * plane (view tilts up toward the horizon); decreasing it lifts toward a
 * bird's-eye. A zero-length offset has no defined direction and is returned
 * unchanged (no divide-by-zero).
 */
export function tiltOffset(offset: Vec3, dPolar: number, minPolar: number, maxPolar: number): Vec3 {
  const radius = Math.hypot(offset.x, offset.y, offset.z);
  if (radius < EPS) return { x: offset.x, y: offset.y, z: offset.z };
  const horiz = Math.hypot(offset.x, offset.z);
  const polar = Math.atan2(horiz, offset.y);
  const azimuth = Math.atan2(offset.z, offset.x);
  const newPolar = clamp(polar + dPolar, minPolar, maxPolar);
  const sinP = Math.sin(newPolar);
  return {
    x: radius * sinP * Math.cos(azimuth),
    y: radius * Math.cos(newPolar),
    z: radius * sinP * Math.sin(azimuth),
  };
}
